'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { api } from '@/lib/api/client'
import type { MotivoSaida } from '@/lib/auth/avisoDeLogin'
import { ouvirCanal } from '@/lib/auth/canalEntreAbas'
import { onGrantsChanged } from '@/lib/auth/grantsChanged'
import { encerrarSessaoLocal, seguirSaidaDeOutraAba } from '@/lib/auth/saida'
import { sairNoServidor, type ConfirmacaoDeSaida } from '@/lib/auth/saidaNoServidor'
import { CHAVE_SAIDA_PENDENTE, anunciarOperacao, estaAbaEstaSaindo, retirarOperacao } from '@/lib/auth/sessaoEntreAbas'
import { disconnectSocket, pausarSocket, retomarSocket } from '@/lib/socket'
import type { AuthUser } from '@/types/auth'

const AUTH_USER_KEY = 'auth_user'
const REFRESH_INTERVAL_MS = 5 * 60 * 1000

export type ResultadoSairDeTodos = 'saiu' | 'falhou'

interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  /**
   * `true` enquanto o `user` é o snapshot do localStorage e o PRIMEIRO `/auth/me` da sessão ainda não
   * respondeu. O snapshot pode estar velho (grant revogado com a máquina fora): gate que renderiza por
   * permission em cima dele mostra a ação e depois some. Só a primeira revalidação conta.
   */
  isRevalidating: boolean
  refresh: () => Promise<void>
  /** "Sair" (faixa 25 §6). Nunca rejeita: a saída local acontece sempre; sem confirmação do back fica a saída pendente (D5). */
  logout: () => Promise<void>
  /** "Sair de todos os dispositivos". `'falhou'` = o back não confirmou: nada mudou, a aba FICA (D6/D13 da faixa 22). */
  sairDeTodos: () => Promise<ResultadoSairDeTodos>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRevalidating, setIsRevalidating] = useState(true)
  // Uma saída por documento: duplo clique, o canal e o `storage` da marca não podem sair duas vezes.
  const saindo = useRef(false)

  const refresh = useCallback(async () => {
    const { data } = await api.get<AuthUser>('/auth/me')
    setUser(data)
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data))
  }, [])

  /** A saída local depois de o servidor responder (ou não). Limpeza e navegação moram em `lib/auth/saida.ts`. */
  const sairDestaAba = useCallback(
    (confirmacao: ConfirmacaoDeSaida) => {
      disconnectSocket()
      encerrarSessaoLocal({
        motivo: 'saiu',
        userId: user?.user_id,
        avisarNestaAba: false,
        saidaPendente: confirmacao === 'nao-confirmada',
      })
    },
    [user],
  )

  const logout = useCallback(async () => {
    if (saindo.current || estaAbaEstaSaindo()) return
    saindo.current = true
    // Anúncio ANTES do pedido (D9): quem cair enquanto ele vale espera e lê o motivo.
    anunciarOperacao('sair')
    // Pausa intencional: a queda que o próprio "Sair" provoca chega como `io client disconnect`.
    pausarSocket()
    // Dentro do trinco, com 401 → renova → repete e teto de 3 s (I14). Saída local SEMPRE (D10 da faixa 22).
    sairDestaAba(await sairNoServidor('logout'))
  }, [sairDestaAba])

  const sairDeTodos = useCallback(async (): Promise<ResultadoSairDeTodos> => {
    if (saindo.current || estaAbaEstaSaindo()) return 'saiu'
    saindo.current = true
    anunciarOperacao('sair-todos')
    pausarSocket()
    const confirmacao = await sairNoServidor('logout-all')
    if (confirmacao === 'nao-confirmada') {
      // D6 da faixa 22: rede, 5xx ou teto — o servidor pode não ter derrubado nada. Sair em silêncio faria a
      // pessoa achar que derrubou os outros dispositivos. Fica e avisa (toast D13 no componente).
      saindo.current = false
      retirarOperacao()
      retomarSocket()
      return 'falhou'
    }
    sairDestaAba(confirmacao)
    return 'saiu'
  }, [sairDestaAba])

  useEffect(() => {
    // O `PortaoDeSessao` só monta este provider DEPOIS da renovação inicial: aqui sempre há sessão.
    const cached = localStorage.getItem(AUTH_USER_KEY)
    if (cached) {
      try {
        setUser(JSON.parse(cached) as AuthUser)
        setIsLoading(false)
      } catch {
        localStorage.removeItem(AUTH_USER_KEY)
      }
    }
    refresh()
      .catch(() => {}) // 401 o interceptor renova e repete; outros erros mantêm o snapshot
      .finally(() => {
        setIsLoading(false)
        // Assenta mesmo em erro: ficar "revalidando" pra sempre esconderia toda ação gated sem rede.
        setIsRevalidating(false)
      })

    const interval = setInterval(() => {
      refresh().catch(() => {})
    }, REFRESH_INTERVAL_MS)
    // Mutations de grant (users/subroles) notificam aqui → refetch imediato dos próprios grants
    const offGrantsChanged = onGrantsChanged(() => {
      refresh().catch(() => {})
    })
    return () => {
      clearInterval(interval)
      offGrantsChanged()
    }
  }, [refresh])

  useEffect(() => {
    /** Outra aba saiu (§6.4): segue o motivo dela, sem esperar 401 (I8). */
    const seguir = (motivo?: MotivoSaida) => {
      if (saindo.current || estaAbaEstaSaindo()) return
      saindo.current = true
      disconnectSocket()
      seguirSaidaDeOutraAba(motivo)
    }
    const desligarCanal = ouvirCanal((mensagem) => {
      if (mensagem.tipo === 'saiu') seguir(mensagem.motivo)
    })
    // Reserva do sinal (§6): aba congelada que perdeu a mensagem vê a marca de saída pendente NASCER. A marca
    // sendo apagada (saída concluída) não derruba ninguém.
    const aoMudarStorage = (e: StorageEvent) => {
      if (e.key === CHAVE_SAIDA_PENDENTE && e.newValue !== null) seguir()
    }
    window.addEventListener('storage', aoMudarStorage)
    return () => {
      desligarCanal()
      window.removeEventListener('storage', aoMudarStorage)
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, isLoading, isRevalidating, refresh, logout, sairDeTodos }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return ctx
}
